import React, {
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";

import {
  Box,
  TextField,
  InputAdornment,
  Snackbar,
  Alert
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import AppButton from "../ui/AppButton";
import PageHeader from "../ui/PageHeader";
import DeleteDialog from "../modals/DeleteDialog";
import ProjectTable from "../components/projects/ProjectTable";
import ProjectDialog from "../components/projects/ProjectDialog";
import ProjectImportDialog from "../components/projects/ProjectImportDialog";
import ProjectAssignDialog from "../components/projects/ProjectAssignDialog";

import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  publishProject
} from "../services/projectService";
import { useUserRole } from "../context/UserRoleContext";
import { canWritePage, hasOwnDataScope } from "../auth/roles";

function Projects() {
  const { role, employee } = useUserRole();

  const canManageProjects = canWritePage(role, "projects");

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const assignedTo = hasOwnDataScope(role, "projects") ? (employee?.employee_id ?? -1) : null;

      // Fetched separately: the "assigned_to" scoping filter only makes sense for
      // Published projects — a creator's own draft shouldn't be hidden just
      // because they haven't added themselves as a team member on it yet.
      const [publishedData, draftData] = await Promise.all([
        getProjects("", assignedTo, false, "Published"),
        getProjects("", null, false, "Draft")
      ]);

      const myDrafts = draftData.filter((p) => p.created_by === employee?.employee_id);
      const visible = [...publishedData, ...myDrafts].sort((a, b) => b.project_id - a.project_id);
      setProjects(visible);

      setSelectedProjectIds((prev) => {
        const validIds = new Set(visible.map((project) => project.project_id));
        return prev.filter((id) => validIds.has(id));
      });
    }
    catch (error) {
      console.error("Project Load Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load projects",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [role, employee]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      p.project_name?.toLowerCase().includes(q) ||
      String(p.project_id).includes(q)
    );
  }, [projects, search]);
useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  
  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = { ...data };

      if (selectedProject) {
        await updateProject(selectedProject.project_id, payload);
      }
      else {
        await createProject(payload);
      }

      setDialogOpen(false);
      setSelectedProject(null);

      await loadProjects();

      setSnackbar({
        open: true,
        message: "Project saved successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Save Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteProject(selectedProject.project_id);
      await loadProjects();

      setDeleteOpen(false);
      setSelectedProject(null);

      setSnackbar({
        open: true,
        message: "Project deleted successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Delete Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Delete failed",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjectIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const projectId of selectedProjectIds) {
        try {
          await deleteProject(projectId);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      await loadProjects();
      setSelectedProjectIds([]);

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} project(s) deleted successfully`,
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning"
        });
      }
    } catch (error) {
      console.error(error);
      setBulkDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Bulk delete failed",
        severity: "error"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handlePublish = async (project) => {
    if (isPublishing) {
      return;
    }

    try {
      setIsPublishing(true);
      await publishProject(project.project_id);
      await loadProjects();

      setSnackbar({
        open: true,
        message: "Project published and assigned users notified",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Publish Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Publish failed",
        severity: "error"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3
      }}
    >
      <PageHeader
        title="Projects"
        subtitle="Create projects and assign them to employees."
        actions={canManageProjects && (
          <>
            <AppButton
              variant="outlined"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => setImportOpen(true)}
            >
              Import from Excel
            </AppButton>

            <AppButton
              variant="outlined"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => setAssignOpen(true)}
            >
              Assign from Excel
            </AppButton>

            <AppButton
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              loading={isBulkDeleting}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={selectedProjectIds.length === 0}
            >
              {isBulkDeleting ? "Deleting..." : `Delete All (${selectedProjectIds.length})`}
            </AppButton>

            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedProject(null);
                setDialogOpen(true);
              }}
            >
              Create Project
            </AppButton>
          </>
        )}
      />

      <Box
        sx={{
          mb: 2,
          maxWidth: 320
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search projects"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <ProjectTable
        projects={filteredProjects}
        loading={loading}
        readOnly={!canManageProjects}
        selectedIds={selectedProjectIds}
        onSelectionChange={setSelectedProjectIds}
        onPublish={handlePublish}
        onEdit={(project) => {
          setSelectedProject(project);
          setDialogOpen(true);
        }}
        onDelete={(project) => {
          setSelectedProject(project);
          setDeleteOpen(true);
        }}
      />

      <ProjectAssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onDone={() => loadProjects()}
      />

      <ProjectImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => loadProjects()}
      />

      <ProjectDialog
        open={dialogOpen}
        project={selectedProject}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedProject(null);
        }}
        onSave={handleSave}
      />

      <DeleteDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        title="Delete Project"
        message="Are you sure you want to delete this project?"
        itemName={selectedProject?.project_name}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          setDeleteOpen(false);
          setSelectedProject(null);
        }}
        onConfirm={handleDelete}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        onConfirm={handleBulkDelete}
        isDeleting={isBulkDeleting}
        title="Delete Selected Projects"
        message="Are you sure you want to delete all selected projects?"
        itemName={`Selected: ${selectedProjectIds.length}`}
        confirmLabel="Delete All"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Projects;
